import { supabase } from './supabase';

export const systemSettingsService = {
  /**
   * Get all system settings. Auto-initializes if not exists.
   */
  async getSettings() {
    const { data, error } = await supabase.rpc('get_system_settings');

    if (error) throw error;
    return data;
  },

  /**
   * Update competition defaults (apparatus order, scoring rules, timezone, etc.)
   */
  async updateCompetitionDefaults(defaults) {
    // Get current settings first for audit log
    const current = await this.getSettings();

    const { data, error } = await supabase
      .from('system_settings')
      .update({ competition_defaults: defaults })
      .select()
      .single();

    if (error) throw error;

    // Log the change
    await supabase.rpc('log_audit_event', {
      p_action: 'settings.competition_defaults_updated',
      p_resource_type: 'settings',
      p_old_value: current.competition_defaults,
      p_new_value: defaults
    });

    return data;
  },

  /**
   * Update user defaults (default role, auto-approve, etc.)
   */
  async updateUserDefaults(defaults) {
    const current = await this.getSettings();

    const { data, error } = await supabase
      .from('system_settings')
      .update({ user_defaults: defaults })
      .select()
      .single();

    if (error) throw error;

    await supabase.rpc('log_audit_event', {
      p_action: 'settings.user_defaults_updated',
      p_resource_type: 'settings',
      p_old_value: current.user_defaults,
      p_new_value: defaults
    });

    return data;
  },

  /**
   * Update feature flags (usag_import_enabled, public_scoreboard_enabled, etc.)
   */
  async updateFeatureFlags(flags) {
    const current = await this.getSettings();

    const { data, error } = await supabase
      .from('system_settings')
      .update({ feature_flags: flags })
      .select()
      .single();

    if (error) throw error;

    await supabase.rpc('log_audit_event', {
      p_action: 'settings.feature_flags_updated',
      p_resource_type: 'settings',
      p_old_value: current.feature_flags,
      p_new_value: flags
    });

    return data;
  },

  /**
   * Get a single feature flag value
   */
  async getFeatureFlag(flagName) {
    try {
      const settings = await this.getSettings();
      return settings?.feature_flags?.[flagName] ?? false;
    } catch (error) {
      console.error('Error getting feature flag:', error);
      return false;
    }
  },

  /**
   * Toggle a single feature flag
   */
  async toggleFeatureFlag(flagName) {
    const settings = await this.getSettings();
    const currentValue = settings?.feature_flags?.[flagName] ?? false;

    const updatedFlags = {
      ...settings.feature_flags,
      [flagName]: !currentValue
    };

    return this.updateFeatureFlags(updatedFlags);
  }
};
